import { getSearchApiBaseUrl, getAuthHeader } from "ui";

/**
 * Fire-and-forget ping that asks the backend to seed a new user's account with
 * starter content (concepts, boards, a collection, and a "Soho Studio" project).
 *
 * Mirrors checkSignupBonus in packages/ui/utils/useCreditStore.ts: deduped per
 * uid, flags on success only (transient failures may retry), never throws. The
 * backend (POST /api/onboarding/seed-starter-content) is idempotent and runs in
 * every environment, cloning that env's own template docs (see
 * ExpressApp/src/config/starterTemplates.ts), so calling more than once is safe.
 */

let seededForUid: string | null = null;
let inFlightUid: string | null = null;
let inFlight: Promise<boolean> | null = null;

export async function seedStarterContent(uid: string): Promise<boolean> {
  if (!uid) return false;
  if (seededForUid === uid) return false;
  if (inFlight && inFlightUid === uid) return inFlight;

  inFlightUid = uid;
  inFlight = (async () => {
    try {
      const base = getSearchApiBaseUrl();
      const res = await fetch(`${base}/api/onboarding/seed-starter-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(await getAuthHeader()),
        },
      });
      if (!res.ok) {
        // A failed attempt is not flagged, so a later trigger can retry.
        console.warn(`[seedStarterContent] request failed: ${res.status}`);
        return false;
      }
      const data = (await res.json()) as { seeded?: boolean };
      const seeded = data?.seeded === true;
      // Flag on confirmed success only — the backend returns HTTP 200 with
      // seeded:false on an internal failure, and that must stay retriable.
      if (seeded && inFlightUid === uid) seededForUid = uid;
      return seeded;
    } catch (err) {
      console.warn("[seedStarterContent] ping failed:", err);
      return false;
    } finally {
      if (inFlightUid === uid) {
        inFlight = null;
        inFlightUid = null;
      }
    }
  })();

  return inFlight;
}
